/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — custom edge, rebuilt on React Flow's NATIVE edge
   primitives (People Rebuild §3, binding architecture):

     • BaseEdge renders the actual <path> + the interaction/hit-halo
       path (its built-in `interactionWidth` replaces the old
       hand-drawn 20px-halo <path>).
     • EdgeLabelRenderer renders the label chip — replaces the old
       hand-drawn SVG <rect>/<text> pill.
     • Hand-rolled geometry (edgeGeometry.ts) builds every path: there
       is only ONE line style now (People Rebuild orthogonal-lines law,
       2026-08-18 — the Rounded/Square/Straight/Curved selector is
       gone), so `getStraightPath`/`getBezierPath` have nothing left to
       draw here.

   ORTHOGONAL LINES LAW (Josh's exact formula, 2026-08-18: "shortest
   path, fewest turns, but only 90-degree perpendicular changes —
   diagonal lines are impossible; if close enough to aligned, snap to a
   single straight line") — a plain auto edge (no waypoints, no
   trunk/bus jog) is ONE straight segment when its anchors are aligned
   within `AXIS_SNAP_PX`, else the fewest-turn L/Z `buildAutoOrthoRoute`
   computes (`buildEdgePoints`, edgeGeometry.ts). Every polyline —
   auto, manual waypoints, or a live drag preview — is run through
   `normalizePolyline`/`getEdgePolyline` immediately before it's used
   for hit-testing or handed to a path builder, so a diagonal reaching
   the screen is structurally impossible, not just usually avoided.

   Segment drag is axis-locked: `dragSegment` (edgeGeometry.ts) can only
   move a corner along ONE axis per call, so it can never itself
   introduce a diagonal. Turn drag is free 2D — the live preview
   re-squares through `normalizePolyline` every frame, and the persisted
   waypoints on release are the NORMALIZED corner list (what you see is
   what saves), not the raw drag point. */

import React, { useState } from 'react';
import { rem } from '../../../../lib/rem';
import {
  BaseEdge,
  EdgeLabelRenderer,
  useReactFlow,
  type EdgeProps,
  type EdgeTypes,
} from '@xyflow/react';
import {
  buildEdgePoints,
  buildHoppedPath,
  buildWaypointPath,
  dragSegment,
  endpointFlex,
  getEdgePolyline,
  normalizePolyline,
  snapTurnPosition,
  type Pt,
} from '../edgeGeometry';
import { useOrgChartCanvas } from '../context/OrgChartCanvasContext';
import type { OrgEdgeData, OrgEdgeLineStyle } from '../types';

const SNAP_THRESHOLD = 8;

function snapPoint(p: Pt, targets: { xs: number[]; ys: number[] }): Pt {
  let x = p.x;
  let y = p.y;
  let bestDx = SNAP_THRESHOLD;
  for (const t of targets.xs) {
    const d = Math.abs(p.x - t);
    if (d < bestDx) {
      bestDx = d;
      x = t;
    }
  }
  let bestDy = SNAP_THRESHOLD;
  for (const t of targets.ys) {
    const d = Math.abs(p.y - t);
    if (d < bestDy) {
      bestDy = d;
      y = t;
    }
  }
  return { x, y };
}

export function OrgEdgeV2(props: EdgeProps) {
  const {
    id,
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected,
    style,
    markerEnd,
  } = props;
  const d = (data || {}) as OrgEdgeData;
  const edgeId = d.edgeId || id;
  const waypoints = d.waypoints || [];
  // Auto-routing (People Rebuild, Josh grant 2026-08-17): an edge with
  // no MANUAL waypoints uses the router's computed polyline instead —
  // render-only, injected by OrgChartV2 (never persisted; `waypoints`
  // itself stays whatever the DB has, empty for an auto edge). Once
  // `waypoints` is non-empty this line is manual and autoWaypoints is
  // ignored, matching the "empty waypoints = auto" rule exactly.
  const effectivePoints = waypoints.length > 0 ? waypoints : d.autoWaypoints || [];
  const canDrag = !!d.canEdit;
  // Turn-drag (free 2D reposition of a REAL waypoint) shares the same
  // gate now that there's only one line style (People Rebuild
  // orthogonal-lines law, 2026-08-18 — segment-drag used to be gated to
  // the orthogonal routings only; every routing is orthogonal now).
  const canDragTurns = !!d.canEdit;
  const isManual = waypoints.length > 0;

  const { screenToFlowPosition } = useReactFlow();
  const { snapEnabled, snapTargetsRef } = useOrgChartCanvas();
  const [dragPreview, setDragPreview] = useState<Pt[] | null>(null);

  const sourcePt: Pt = { x: sourceX, y: sourceY };
  const targetPt: Pt = { x: targetX, y: targetY };
  const allPoints = buildEdgePoints(
    sourcePt,
    targetPt,
    effectivePoints,
    endpointFlex(source),
    endpointFlex(target),
    sourcePosition,
    targetPosition
  );
  // THE normalize point (People Rebuild orthogonal-lines law,
  // 2026-08-18) — `corners` is `allPoints` post-`normalizePolyline`
  // (via `getEdgePolyline`); `liveCorners` re-runs it over a live drag
  // preview too, so a diagonal segment reaching the screen is
  // structurally impossible regardless of which of the two produced
  // this frame's polyline.
  const corners = getEdgePolyline(allPoints);
  const liveCorners = normalizePolyline(dragPreview || corners);
  const hasHops = (d.hopsBySeg?.size ?? 0) > 0;

  // ─── Path: hops get the hand-rolled hop-arc renderer; everything
  // else (2-point straight, auto L/Z, manual waypoints, a drag
  // preview) is one orthogonal corner list — `buildWaypointPath`
  // handles the 2-point case as a plain line and rounds every real
  // interior corner otherwise, so there's exactly one path builder for
  // the one surviving line style. ───
  const pathD = hasHops
    ? buildHoppedPath(liveCorners, dragPreview ? undefined : d.hopsBySeg)
    : buildWaypointPath(liveCorners);
  const previewPathD = dragPreview ? buildHoppedPath(liveCorners) : null;

  const highlight = d.highlight || 'none';
  const stroke = d.color || (style as React.CSSProperties)?.stroke || 'var(--tx)';
  const isDimmed = highlight === 'dimmed';
  const isActive = highlight === 'active' || !!selected;
  const strokeWidth = isActive ? 3 : 1.5;
  const lineStyle: OrgEdgeLineStyle = d.lineStyle || 'solid';
  const dashArray = lineStyle === 'dashed' ? '8 4' : lineStyle === 'dotted' ? '2 4' : undefined;

  const midIdx = Math.floor(corners.length / 2);
  const midA = corners[midIdx - 1] || corners[0];
  const midB = corners[midIdx] || corners[corners.length - 1];
  const labelX = (midA.x + midB.x) / 2;
  const labelY = (midA.y + midB.y) / 2;

  const setHover = (hovering: boolean) => d.onHoverChange?.(edgeId, hovering);

  // ─── Segment drag (bend) — axis-locked, feeds BaseEdge a hand-built
  // path string via dragPreview while live. ───
  const onSegPointerDown = (e: React.PointerEvent, segIndex: number, axis: 'H' | 'V') => {
    if (!canDrag) return;
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget as SVGElement;
    target.setPointerCapture(e.pointerId);
    const start = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const baseCorners = corners;
    const onMove = (ev: PointerEvent) => {
      const cur = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const delta = axis === 'H' ? cur.y - start.y : cur.x - start.x;
      let next = dragSegment(baseCorners, segIndex, axis, delta);
      if (snapEnabled) {
        next = next.map((p, i) =>
          i === 0 || i === next.length - 1 ? p : snapPoint(p, snapTargetsRef.current)
        );
      }
      setDragPreview(next);
    };
    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(e.pointerId);
      const cur = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const delta = axis === 'H' ? cur.y - start.y : cur.x - start.x;
      let next = dragSegment(baseCorners, segIndex, axis, delta);
      if (snapEnabled) {
        next = next.map((p, i) =>
          i === 0 || i === next.length - 1 ? p : snapPoint(p, snapTargetsRef.current)
        );
      }
      setDragPreview(null);
      // PERSIST the normalized corner list, not the raw drag result —
      // a no-op here (`dragSegment` is already axis-locked-orthogonal)
      // but the same rule turn-drag needs below, applied uniformly.
      const nextWaypoints = normalizePolyline(next).slice(1, -1);
      d.onWaypointsChange?.(edgeId, nextWaypoints);
      target.removeEventListener('pointermove', onMove as EventListener);
      target.removeEventListener('pointerup', onUp as EventListener);
    };
    target.addEventListener('pointermove', onMove as EventListener);
    target.addEventListener('pointerup', onUp as EventListener, { once: true });
  };

  // ─── Turn drag (People Rebuild 2026-08-18, item 2) — free 2D
  // reposition of a REAL waypoint (`waypoints[wpIndex]`, cornerIndex =
  // wpIndex+1 since `corners[0]` is the source anchor). Not axis-locked
  // like segment drag: near-axis snap is relative to the turn's OWN
  // two neighbors (`snapTurnPosition`), then the optional global
  // alignment-guide snap on top, same layering `onSegPointerDown` uses. ───
  const onTurnPointerDown = (e: React.PointerEvent, wpIndex: number) => {
    if (!canDragTurns) return;
    e.stopPropagation();
    e.preventDefault();
    const target = e.currentTarget as SVGElement;
    target.setPointerCapture(e.pointerId);
    // `allPoints` (RAW [source, ...waypoints, target], not the
    // normalized `corners`) — `wpIndex + 1` only reliably indexes the
    // waypoint being dragged here. `corners` can have EXTRA synthetic
    // elbow points ahead of it (a legacy edge saved under the old
    // diagonal-permitted model can still have a diagonal leg on disk
    // until this drag heals it), which would silently misalign
    // `cornerIndex` against the wrong point.
    const baseCorners = allPoints;
    const cornerIndex = wpIndex + 1;
    const prevPt = baseCorners[cornerIndex - 1];
    const nextPt = baseCorners[cornerIndex + 1];
    const resolve = (ev: PointerEvent): Pt[] => {
      const cur = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      let snapped = snapTurnPosition(cur, prevPt, nextPt);
      if (snapEnabled) snapped = snapPoint(snapped, snapTargetsRef.current);
      const next = [...baseCorners];
      next[cornerIndex] = snapped;
      return next;
    };
    const onMove = (ev: PointerEvent) => setDragPreview(resolve(ev));
    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(e.pointerId);
      const next = resolve(ev);
      setDragPreview(null);
      // Turn drag is free 2D — the dragged point can land off-axis from
      // both neighbors. PERSIST the NORMALIZED corner list (what the
      // live preview re-squared to and what got rendered), not the raw
      // drag point — a raw off-axis point would be a waypoint with no
      // corresponding rendered vertex (People Rebuild orthogonal-lines
      // law, 2026-08-18: "what you see is what saves").
      d.onWaypointsChange?.(edgeId, normalizePolyline(next).slice(1, -1));
      target.removeEventListener('pointermove', onMove as EventListener);
      target.removeEventListener('pointerup', onUp as EventListener);
    };
    target.addEventListener('pointermove', onMove as EventListener);
    target.addEventListener('pointerup', onUp as EventListener, { once: true });
  };

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <>
      <BaseEdge
        id={id}
        path={pathD}
        markerEnd={markerEnd}
        interactionWidth={20}
        style={{
          stroke: isActive ? 'var(--ac)' : stroke,
          strokeWidth,
          strokeDasharray: dashArray,
          transition: 'stroke 120ms ease, stroke-width 120ms ease, opacity 120ms ease',
          opacity: isDimmed ? 0.22 : 1,
        }}
      />
      {/* Hover-to-trace trigger — sits on top of BaseEdge's own
          interaction path so hover fires even when the visible stroke
          is hairline. */}
      <path
        d={pathD}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{
          cursor: 'pointer',
          opacity: isDimmed ? 0 : 1,
          pointerEvents: isDimmed ? 'none' : 'stroke',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      />
      {d.flashing && !reduceMotion && (
        <path
          d={pathD}
          fill="none"
          stroke="var(--ac)"
          strokeWidth={strokeWidth}
          style={{ pointerEvents: 'none' }}
        >
          <animate
            attributeName="stroke-width"
            values={`${strokeWidth};${strokeWidth + 3};${strokeWidth}`}
            dur="0.35s"
            repeatCount="2"
          />
        </path>
      )}
      {previewPathD && (
        <path
          d={previewPathD}
          fill="none"
          stroke="var(--ac)"
          strokeWidth={2}
          strokeDasharray="4 3"
          style={{ pointerEvents: 'none' }}
        />
      )}
      {isActive && !isDimmed && (
        <>
          {[sourcePt, targetPt].map((p, i) => (
            <g key={i}>
              <circle
                cx={p.x}
                cy={p.y}
                r={16}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={(e) => {
                  e.stopPropagation();
                  d.onEndpointClick?.(edgeId);
                }}
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={4}
                fill="var(--ac)"
                stroke="var(--sf)"
                strokeWidth={1.5}
                style={{ pointerEvents: 'none' }}
              />
            </g>
          ))}
        </>
      )}
      {selected &&
        canDrag &&
        liveCorners.slice(0, -1).map((a, i) => {
          const b = liveCorners[i + 1];
          const axis: 'H' | 'V' = Math.abs(a.y - b.y) < 0.5 ? 'H' : 'V';
          const midX = (a.x + b.x) / 2;
          const midY = (a.y + b.y) / 2;
          return (
            <g key={i}>
              {/* Full-segment grab strip — the ENTIRE segment is the bend
                  hit area (People Rebuild ergonomics fix, Josh incident
                  2026-08-17: the old 16px midpoint-only zone made users
                  hunt pixel-by-pixel). strokeWidth 24 (>=24px per spec)
                  along the whole a→b run, cursor set per axis. */}
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="transparent"
                strokeWidth={24}
                aria-label="Drag this line segment to bend it"
                style={{ cursor: axis === 'H' ? 'ns-resize' : 'ew-resize' }}
                onPointerDown={(e) => onSegPointerDown(e, i, axis)}
              />
              {/* Visible bend dot at the segment midpoint — >=10px
                  diameter (r=5), purely visual; the strip above is the
                  hit area, this no longer needs its own separate
                  16px-circle hit zone. */}
              <circle
                cx={midX}
                cy={midY}
                r={5}
                fill="var(--sf)"
                stroke="var(--ac)"
                strokeWidth={2}
                style={{ pointerEvents: 'none' }}
              />
            </g>
          );
        })}
      {/* Turn dots (People Rebuild 2026-08-18, item 2) — one per REAL
          waypoint, visible whenever the edge is selected. Bigger than
          the segment bend dots above (r=6 vs r=5, filled solid accent
          instead of outline) so a turn always reads as the more
          deliberate, "this is a real point you placed" affordance;
          hit target is a separate >=28px-diameter circle (r=14) so it
          doesn't compete with the segment-drag strips underneath. Only
          rendered for a MANUAL edge — an auto/tree-routed bus corner
          isn't a turn (there's no waypoint backing it). Positioned
          from `allPoints`/`dragPreview` (RAW, 1:1 with `waypoints`),
          never the normalized `liveCorners` — a synthetic elbow
          `normalizePolyline` inserts for a legacy diagonal leg is not
          a turn and must never get its own dot or steal a wpIndex. */}
      {selected &&
        canDragTurns &&
        isManual &&
        (dragPreview || allPoints).slice(1, -1).map((p, wpIndex) => (
          <g key={`turn-${wpIndex}`}>
            <circle
              cx={p.x}
              cy={p.y}
              r={14}
              fill="transparent"
              aria-label="Drag to move this turn, right-click to delete it"
              style={{ cursor: 'move' }}
              onPointerDown={(e) => onTurnPointerDown(e, wpIndex)}
            />
            <circle
              cx={p.x}
              cy={p.y}
              r={6}
              fill="var(--ac)"
              stroke="var(--sf)"
              strokeWidth={2}
              style={{ pointerEvents: 'none' }}
            />
          </g>
        ))}
      {/* Native label chip (People Rebuild §3 — EdgeLabelRenderer, not
          a hand-drawn SVG pill). */}
      {d.label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              opacity: isDimmed ? 0.22 : 1,
              background: 'var(--sf)',
              border: `1px solid ${isActive ? 'var(--ac)' : stroke}`,
              borderRadius: rem(9),
              padding: '1px var(--space-2)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              color: 'var(--tx)',
              whiteSpace: 'nowrap',
            }}
          >
            {d.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const edgeTypes: EdgeTypes = { org: OrgEdgeV2 };
